import { useCallback, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Upload } from "lucide-react";
import {
  APPOINTMENT_DB_FIELDS,
  autoMapAppointmentColumns,
  type AppointmentImportResult,
} from "@bookdental/schemas";
import { Button, Switch, toast } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { FileDropzone } from "./FileDropzone";
import { Stepper } from "./Stepper";
import { MappingTable } from "./MappingTable";
import { ImportResultView } from "./ImportResultView";
import { ImportHistoryList } from "./ImportHistoryList";
import { parseImportFile, previewRows, type ParsedFile } from "./lib/parseFile";
import { buildAppointmentRecords } from "./lib/buildAppointmentRecords";
import {
  ErroImportacaoParcial,
  importarEmLotes,
  type ProgressoImport,
} from "./lib/chunked-import";

const STEPS = ["Arquivo", "Mapeamento", "Importar"];
type Step = 0 | 1 | 2;

export function ImportAppointmentsSection() {
  const [step, setStep] = useState<Step>(0);
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [onlyFuture, setOnlyFuture] = useState(true);
  const [parsing, setParsing] = useState(false);
  const [result, setResult] = useState<AppointmentImportResult | null>(null);
  const [progresso, setProgresso] = useState<ProgressoImport | null>(null);

  const importMutation = trpc.import.appointments.useMutation();
  const utils = trpc.useUtils();

  const sampleRow = useMemo(
    () => (parsed ? previewRows(parsed, 1)[0] : undefined),
    [parsed],
  );
  const mappedCount = Object.values(mappings).filter((v) => v && v !== "ignore").length;
  const isPatientNameMapped = Object.values(mappings).includes("patientName");
  const isDateMapped = Object.values(mappings).includes("date");

  const reset = useCallback(() => {
    setStep(0);
    setFile(null);
    setParsed(null);
    setMappings({});
    setOnlyFuture(true);
    setResult(null);
  }, []);

  const handleParse = useCallback(async () => {
    if (!file) return;
    setParsing(true);
    try {
      const p = await parseImportFile(file);
      setParsed(p);
      setMappings(autoMapAppointmentColumns(p.headers));
      setStep(1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao ler arquivo");
    } finally {
      setParsing(false);
    }
  }, [file]);

  const handleImport = useCallback(async () => {
    if (!parsed) return;
    const records = buildAppointmentRecords(parsed, mappings);
    if (records.length === 0) {
      toast.error("Nenhum registro valido pra importar (exige nome e data).");
      return;
    }
    const invalidar = () => {
      utils.appointment.listByRange.invalidate();
      utils.patient.list.invalidate();
      utils.import.listBatches.invalidate();
    };
    try {
      const res = await importarEmLotes(
        records,
        (lote) => importMutation.mutateAsync({ records: lote, onlyFuture, fileName: file?.name }),
        setProgresso,
      );
      setResult(res);
      invalidar();
      toast.success(`Importacao concluida: ${res.created} consultas criadas.`);
    } catch (err) {
      // Ver ImportPatientsSection: importação parcial não pode parecer falha total.
      if (err instanceof ErroImportacaoParcial && err.parcial) {
        setResult(err.parcial as AppointmentImportResult);
        invalidar();
        toast.error(
          `Importacao interrompida apos ${err.parcial.totalProcessed} registros. ` +
            `As ja importadas foram mantidas — reenvie so' o restante da planilha.`,
        );
        return;
      }
      toast.error(err instanceof Error ? err.message : "Erro na importacao");
    } finally {
      setProgresso(null);
    }
  }, [parsed, mappings, onlyFuture, importMutation, utils, file]);

  const busy = parsing || importMutation.isPending || progresso !== null;

  return (
    <div className="space-y-8">
      <section className="space-y-5">
        <SectionHeader
          title="Importacao"
          subtitle="Envie um arquivo CSV ou Excel pra importar consultas."
        />
        <Stepper steps={STEPS} current={step} />

      {step === 0 && (
        <div className="space-y-4">
          <FileDropzone file={file} onFile={setFile} disabled={busy} />
          <div className="flex justify-end">
            <Button onClick={handleParse} disabled={!file || busy}>
              {parsing ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowRight className="mr-1.5 h-3.5 w-3.5" />
              )}
              {parsing ? "Lendo..." : "Proximo"}
            </Button>
          </div>
        </div>
      )}

      {step === 1 && parsed && (
        <div className="space-y-4">
          <MappingTable
            headers={parsed.headers}
            sampleRow={sampleRow}
            dbFields={APPOINTMENT_DB_FIELDS}
            mappings={mappings}
            onChange={(col, value) =>
              setMappings((prev) => {
                const next = { ...prev };
                if (!value || value === "ignore") delete next[col];
                else next[col] = value;
                return next;
              })
            }
            requiredField="patientName"
            requiredLabel="Nome do paciente"
            totalRows={parsed.totalRows}
          />

          <div className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
            <Switch checked={onlyFuture} onCheckedChange={setOnlyFuture} />
            <div>
              <p className="text-xs font-semibold text-zinc-900">
                Apenas consultas futuras
              </p>
              <p className="mt-0.5 text-[11px] text-zinc-500">
                Ignora consultas com data anterior a hoje.
              </p>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(0)}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Voltar
            </Button>
            <Button
              onClick={() => setStep(2)}
              disabled={!isPatientNameMapped || !isDateMapped}
            >
              <ArrowRight className="mr-1.5 h-3.5 w-3.5" /> Proximo
            </Button>
          </div>
          {(!isPatientNameMapped || !isDateMapped) && (
            <p className="text-[11px] text-amber-700">
              Nome do paciente e Data sao campos obrigatorios.
            </p>
          )}
        </div>
      )}

      {step === 2 && parsed && !result && (
        <div className="space-y-4">
          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <p className="mb-2 text-xs font-semibold text-zinc-900">Pronto pra importar</p>
            <dl className="space-y-1.5 text-xs text-zinc-700">
              <div className="flex items-center justify-between">
                <dt className="text-zinc-500">Registros a processar</dt>
                <dd className="font-medium text-zinc-900">{parsed.totalRows}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-zinc-500">Colunas mapeadas</dt>
                <dd className="font-medium text-zinc-900">{mappedCount}</dd>
              </div>
              {onlyFuture && (
                <div className="flex items-center justify-between">
                  <dt className="text-zinc-500">Filtro</dt>
                  <dd className="font-medium text-zinc-900">Apenas consultas futuras</dd>
                </div>
              )}
            </dl>
            <p className="mt-3 text-[10px] text-zinc-500">
              Pacientes nao encontrados serao criados automaticamente.
            </p>
          </div>
          {progresso && progresso.lotes > 1 && (
            <div className="space-y-1.5 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2.5">
              <p className="text-xs text-zinc-600">
                Enviando lote {progresso.lote} de {progresso.lotes} — {progresso.enviados} de{" "}
                {progresso.total} registros
              </p>
              <div className="h-1.5 overflow-hidden rounded-full bg-zinc-200">
                <div
                  className="h-full rounded-full bg-brand-600 transition-all"
                  style={{ width: `${Math.round((progresso.enviados / progresso.total) * 100)}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)} disabled={busy}>
              <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Voltar
            </Button>
            <Button onClick={handleImport} disabled={busy}>
              {importMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="mr-1.5 h-3.5 w-3.5" />
              )}
              {busy ? "Importando..." : "Importar agora"}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && result && (
        <div className="space-y-4">
          <ImportResultView
            totalProcessed={result.totalProcessed}
            chips={[
              { label: `${result.created} consultas criadas`, tone: "success" },
              ...(result.patientsCreated > 0
                ? [{ label: `${result.patientsCreated} pacientes criados`, tone: "info" as const }]
                : []),
              ...(result.patientsMatched > 0
                ? [{ label: `${result.patientsMatched} pacientes vinculados`, tone: "info" as const }]
                : []),
              ...(result.skippedPast > 0
                ? [{ label: `${result.skippedPast} datas passadas`, tone: "warning" as const }]
                : []),
              ...(result.errors > 0
                ? [{ label: `${result.errors} erros`, tone: "error" as const }]
                : []),
            ]}
            errorDetails={result.errorDetails}
          />
          <div className="flex justify-center">
            <Button variant="outline" onClick={reset}>
              Nova importacao
            </Button>
          </div>
        </div>
      )}
      </section>

      <section className="space-y-3">
        <SectionHeader
          title="Importacoes realizadas"
          subtitle="Historico das importacoes feitas. Use 'Apagar' pra remover os registros criados."
        />
        <ImportHistoryList type="APPOINTMENTS" />
      </section>
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="border-b border-zinc-100 pb-2">
      <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
      <p className="mt-0.5 text-xs text-zinc-500">{subtitle}</p>
    </div>
  );
}
